import {
    type DogSex as ContractDogSex,
    type DogSizeBucket as ContractDogSizeBucket,
    dogSexSchema,
    dogSizeBucketSchema,
} from "@wags/wags-api-client/dog.contract.ts";
import {
    getAgeLabelFromMonths,
    getAgeInMonths as getDogAgeInMonths,
    getLifeStageLabel,
} from "@wags/utils/dog-age";

export {
    getAgeLabelFromMonths,
    getDogAgeInMonths as getAgeInMonths,
    getLifeStageLabel,
};

export const DogSex = {
    Male: dogSexSchema.enum.MALE,
    Female: dogSexSchema.enum.FEMALE,
} as const satisfies Record<string, ContractDogSex>;
export type DogSex = ContractDogSex;

export const DogSizeBucket = {
    Mini: dogSizeBucketSchema.enum.MINI,
    Small: dogSizeBucketSchema.enum.SMALL,
    Medium: dogSizeBucketSchema.enum.MEDIUM,
    Large: dogSizeBucketSchema.enum.LARGE,
    Giant: dogSizeBucketSchema.enum.GIANT,
} as const satisfies Record<string, ContractDogSizeBucket>;
export type DogSizeBucket = ContractDogSizeBucket;

export enum DogSexAvailability {
    Male = "MALE",
    Female = "FEMALE",
    Mixed = "MIXED",
    Unknown = "UNKNOWN",
}

const toTimestampMs = (birthday: Date | number) => {
    if (birthday instanceof Date) {
        return birthday.getTime();
    }

    return birthday < 1_000_000_000_000 ? birthday * 1000 : birthday;
};

export const getDogAgeDisplay = (
    birthday: Date | number | null | undefined,
    fallback = "N/A",
) => {
    if (birthday == null) {
        return fallback;
    }

    const birthdayTime = toTimestampMs(birthday);
    if (!Number.isFinite(birthdayTime) || birthdayTime > Date.now()) {
        return fallback;
    }

    const ageInMonths =
        (Date.now() - birthdayTime) / (1000 * 60 * 60 * 24 * 30.44);
    if (ageInMonths < 1) {
        const ageInWeeks =
            (Date.now() - birthdayTime) / (1000 * 60 * 60 * 24 * 7);
        return `${Math.floor(ageInWeeks)}${Math.floor(ageInWeeks) === 1 ? "wk" : "wks"}`;
    }

    if (ageInMonths < 12) {
        return `${Math.floor(ageInMonths)}mon`;
    }

    const ageInYears = ageInMonths / 12;
    return `${Math.floor(ageInYears)}${Math.floor(ageInYears) === 1 ? "yr" : "yrs"}`;
};
export const getDogAgeDisplayVerbose = (
    birthday: Date | number | null | undefined,
    fallback = "N/A",
) => {
    const months = getDogAgeInMonths(birthday);
    const birthdayTimestamp = birthday == null ? null : toTimestampMs(birthday);
    if (months == null) return fallback;
    if (months < 1) {
        if (birthdayTimestamp == null) return fallback;

        const weeks = Math.floor(
            (Date.now() - birthdayTimestamp) / (1000 * 60 * 60 * 24 * 7),
        );
        return `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
    }
    if (months < 12) {
        const m = Math.floor(months);
        return `${m} ${m === 1 ? "month" : "months"}`;
    }

    const years = Math.floor(months / 12);
    return `${years} ${years === 1 ? "year" : "years"}`;
};

export const getDogSexDisplay = (
    sex: DogSex | null | undefined,
    fallback = "N/A",
) => {
    if (sex === DogSex.Male) return "Male";
    if (sex === DogSex.Female) return "Female";
    return fallback;
};

export const getDogSexAvailability = (
    malesAvailable: number | null | undefined,
    femalesAvailable: number | null | undefined,
): DogSexAvailability => {
    const hasMales = (malesAvailable ?? 0) > 0;
    const hasFemales = (femalesAvailable ?? 0) > 0;

    if (hasMales && hasFemales) {
        return DogSexAvailability.Mixed;
    }

    if (hasMales) {
        return DogSexAvailability.Male;
    }

    if (hasFemales) {
        return DogSexAvailability.Female;
    }

    return DogSexAvailability.Unknown;
};

export const getDogSexDisplayFromAvailability = (
    malesAvailable: number | null | undefined,
    femalesAvailable: number | null | undefined,
    fallback = "N/A",
) => {
    const sexAvailability = getDogSexAvailability(
        malesAvailable,
        femalesAvailable,
    );

    if (sexAvailability === DogSexAvailability.Mixed) return "Mixed";
    if (sexAvailability === DogSexAvailability.Male) return "Male";
    if (sexAvailability === DogSexAvailability.Female) return "Female";
    return fallback;
};
export type DistanceFormat = "compact" | "narrative" | "filter-context";

const compactCountFormatter = new Intl.NumberFormat("en-US");

export function formatDistance(
    miles: number | null | undefined,
    format: DistanceFormat = "compact",
): string | null {
    if (miles == null || Number.isNaN(miles)) return null;

    if (miles < 1) {
        return format === "compact" ? "< 1 mi" : "Less than a mile away";
    }

    const rounded = Math.round(miles);
    const count = compactCountFormatter.format(rounded);
    const unit = rounded === 1 ? "mile" : "miles";

    switch (format) {
        case "compact":
            return `${count} mi`;
        case "narrative":
            return `${count} ${unit} away`;
        case "filter-context":
            return `Within ${count} ${unit}`;
    }
}

export function formatCompactCount(count: number): string {
    const rounded = Math.max(0, Math.round(count));
    if (rounded < 10_000) return compactCountFormatter.format(rounded);
    const thousands = rounded / 1_000;
    if (thousands < 100) {
        const k = Math.round(thousands * 10) / 10;
        return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
    }
    return `${Math.round(thousands)}k`;
}

export const getDogSizeDisplayVerbose = (
    size: DogSizeBucket | null | undefined,
): string | null => {
    if (size === DogSizeBucket.Mini) return "Extra Small";
    if (size === DogSizeBucket.Small) return "Small";
    if (size === DogSizeBucket.Medium) return "Medium";
    if (size === DogSizeBucket.Large) return "Large";
    if (size === DogSizeBucket.Giant) return "Extra Large";
    return null;
};

const parseSizeToken = (
    size: string | null | undefined,
): DogSizeBucket | null => {
    if (size === "toy" || size === "mini") return DogSizeBucket.Mini;
    if (size === "small") return DogSizeBucket.Small;
    if (size === "medium") return DogSizeBucket.Medium;
    if (size === "large") return DogSizeBucket.Large;

    if (size === "giant" || size === "extra large") return DogSizeBucket.Giant;
    return null;
};

export const getWeightBucket = (
    size: string | null | undefined,
    sizeBuckets?: readonly string[] | null,
): DogSizeBucket | null => {
    const authoredSize = size?.trim();
    if (authoredSize) return parseSizeToken(authoredSize);
    for (const bucket of sizeBuckets ?? []) {
        const parsed = parseSizeToken(bucket);
        if (parsed) return parsed;
    }
    return null;
};

const TRAINABILITY_LABELS = [
    "Stubborn / difficult",
    "Challenging",
    "Moderate",
    "Easy to train",
    "Very trainable",
];

const EXERCISE_INTENSITY_LABELS = [
    "Leisurely walks",
    "Light activity",
    "Moderate activity",
    "Vigorous activity",
    "Intense (running/hiking)",
];

const EXPERIENCE_LABELS: Record<string, string> = {
    first_time_friendly: "First-time friendly",
    some_experience: "Some experience needed",
    experienced: "Experienced owners",
    expert_only: "Expert only",
};

const HOME_TYPE_LABELS: Record<string, string> = {
    apartment: "Apartment",
    condo: "Condo",
    house: "House",
    acreage: "Acreage",
};

const SHEDDING_LABELS: Record<string, string> = {
    none: "None",
    minimal: "Minimal",
    moderate: "Moderate",
    heavy: "Heavy",
    seasonal_blowout: "Seasonal blowout",
};

function ratingLabel(
    rating: string | null | undefined,
    labels: string[],
): string | null {
    if (!rating) return null;
    const n = Number.parseInt(rating, 10);
    return labels[n - 1] ?? null;
}

export function getBreedCharacteristics(breed: {
    exerciseMinutesDaily?: number | null;
    exerciseIntensity?: string | null;
    trainability?: string | null;
    recommendedExperience?: string | null;
    homeCompatibility?: string[] | null;
    sheddingLevel?: string | null;
    lifespanMinYears?: number | null;
    lifespanMaxYears?: number | null;
}): Array<{ label: string; value: string }> {
    const rows: Array<{ label: string; value: string }> = [];

    if (breed.exerciseMinutesDaily) {
        const intensity = ratingLabel(
            breed.exerciseIntensity,
            EXERCISE_INTENSITY_LABELS,
        );
        rows.push({
            label: "Exercise",
            value: intensity
                ? `${breed.exerciseMinutesDaily} min/day, ${intensity}`
                : `${breed.exerciseMinutesDaily} min/day`,
        });
    }

    const trainability = ratingLabel(breed.trainability, TRAINABILITY_LABELS);
    if (trainability) {
        rows.push({ label: "Trainability", value: trainability });
    }

    const experience = breed.recommendedExperience
        ? (EXPERIENCE_LABELS[breed.recommendedExperience] ??
          breed.recommendedExperience)
        : null;
    if (experience) {
        rows.push({ label: "Experience", value: experience });
    }

    if (breed.homeCompatibility?.length) {
        const homes = breed.homeCompatibility
            .map((h) => HOME_TYPE_LABELS[h] ?? h)
            .join(", ");
        rows.push({ label: "Living space", value: homes });
    }

    if (breed.sheddingLevel) {
        const shedding =
            SHEDDING_LABELS[breed.sheddingLevel] ?? breed.sheddingLevel;
        rows.push({ label: "Shedding", value: shedding });
    }

    const lifespanMin = breed.lifespanMinYears;
    const lifespanMax = breed.lifespanMaxYears;
    if (lifespanMin && lifespanMax) {
        rows.push({
            label: "Lifespan",
            value: `${lifespanMin}-${lifespanMax} years`,
        });
    } else if (lifespanMin ?? lifespanMax) {
        rows.push({
            label: "Lifespan",
            value: `${lifespanMin ?? lifespanMax} years`,
        });
    }

    return rows;
}

export const getShelterAnimalSize = (
    breedSize: string | null | undefined,
): DogSizeBucket => {
    const s = breedSize?.toLowerCase();
    if (s === "small" || s === "toy" || s === "mini")
        return DogSizeBucket.Small;
    if (s === "large" || s === "giant") return DogSizeBucket.Large;
    return DogSizeBucket.Medium;
};

export const getDogSizeDisplayFromBreedSize = (
    breedSize: string | null | undefined,
    fallback = "M",
) => {
    const normalizedBreedSize = breedSize?.toLowerCase();

    if (normalizedBreedSize === "toy" || normalizedBreedSize === "mini") {
        return "XS";
    }

    if (normalizedBreedSize === "small") {
        return "S";
    }

    if (normalizedBreedSize === "medium") {
        return "M";
    }

    if (normalizedBreedSize === "large") {
        return "L";
    }

    if (normalizedBreedSize === "giant") {
        return "XL";
    }

    return fallback;
};

export const getDogSizeDisplayLongFromBreedSize = (
    breedSize: string | null | undefined,
    fallback = "Medium",
) => {
    const normalizedBreedSize = breedSize?.toLowerCase();

    if (normalizedBreedSize === "toy" || normalizedBreedSize === "mini") {
        return "Extra small";
    }

    if (normalizedBreedSize === "small") {
        return "Small";
    }

    if (normalizedBreedSize === "medium") {
        return "Medium";
    }

    if (normalizedBreedSize === "large") {
        return "Large";
    }

    if (normalizedBreedSize === "giant") {
        return "Extra large";
    }

    return fallback;
};
